import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { FaPlus, FaTrash, FaCheck, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import '../styles/projectTaskTable.css';

function ProjectTaskTable({ project }) {
  const [tasks, setTasks] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);

  // Hent opgaver for projektet
  useEffect(() => {
    if (!project?.id) return; // Tjek om project og project.id eksisterer

    const q = query(
      collection(db, 'tasks'),
      where('projectId', '==', project.id)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);
    });

    return () => unsubscribe();
  }, [project?.id]); // Dependency på project?.id i stedet for project.id

  // Opdel opgaver i aktive og løste
  const activeTasks = tasks?.filter(task => !task.completed) || [];
  const completedTasks = tasks?.filter(task => task.completed) || [];

  const handleAddTask = async () => {
    try {
      const newTask = {
        name: '',
        completed: false,
        projectId: project.id,
        createdAt: new Date().toISOString(),
        createdBy: auth.currentUser.uid,
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'tasks'), newTask);
      setEditingTask(docRef.id);
    } catch (error) {
      console.error('Fejl ved oprettelse af opgave:', error);
    }
  };

  const handleTaskNameChange = async (taskId, newName) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        name: newName,
        updatedAt: new Date().toISOString()
      });
      setEditingTask(null);
    } catch (error) {
      console.error('Fejl ved opdatering af opgave navn:', error);
    }
  };

  const toggleTaskStatus = async (taskId, completed) => {
    try {
      const taskRef = doc(db, 'tasks', taskId);
      await updateDoc(taskRef, {
        completed: !completed,
        updatedAt: new Date().toISOString()
      });

      // Tjek om projektet skal opdateres til "doing"
      if (!completed && project.status === 'todo') {
        const hasCompletedTasks = tasks.some(task => task.completed);
        if (!hasCompletedTasks) {
          // Dette bliver den første løste opgave
          const projectRef = doc(db, 'projects', project.id);
          await updateDoc(projectRef, {
            status: 'doing',
            updatedAt: new Date().toISOString()
          });
        }
      }
    } catch (error) {
      console.error('Fejl ved opdatering af opgave status:', error);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await deleteDoc(doc(db, 'tasks', taskId));
    } catch (error) {
      console.error('Fejl ved sletning af opgave:', error);
    }
  };

  const handleKeyPress = async (e, taskId, name) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await handleTaskNameChange(taskId, name);
      // Opret ny opgave efter enter
      handleAddTask();
    }
  };

  return (
    <div className="project-tasks-section">
      <div className="section-header">
        <h2>Opgaver</h2>
        <div className="section-description">
          Administrer projektets opgaver
        </div>
      </div>

      <div className="tasks-table-wrapper">
        <table className="tasks-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>Opgave</th>
              <th style={{ width: '100px' }}>Status</th>
              <th style={{ width: '80px' }}>Handling</th>
            </tr>
          </thead>
          <tbody>
            {/* Aktive opgaver */}
            {activeTasks.map(task => (
              <tr key={task.id} className={task.completed ? 'completed' : ''}>
                <td>
                  <button
                    className={`status-toggle ${task.completed ? 'completed' : ''}`}
                    onClick={() => toggleTaskStatus(task.id, task.completed)}
                  >
                    <FaCheck />
                  </button>
                </td>
                <td>
                  {editingTask === task.id ? (
                    <input
                      type="text"
                      className="task-name-input"
                      defaultValue={task.name}
                      autoFocus
                      onBlur={(e) => handleTaskNameChange(task.id, e.target.value)}
                      onKeyDown={(e) => handleKeyPress(e, task.id, e.target.value)}
                    />
                  ) : (
                    <span 
                      className="task-name"
                      onClick={() => setEditingTask(task.id)}
                    >
                      {task.name || 'Klik for at tilføje navn'}
                    </span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${task.completed ? 'completed' : 'pending'}`}>
                    {task.completed ? 'Udført' : 'Åben'}
                  </span>
                </td>
                <td>
                  <button
                    className="delete-task"
                    onClick={() => deleteTask(task.id)}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}

            {/* Tilføj opgave række */}
            <tr className="add-task-row">
              <td colSpan="4">
                <button onClick={handleAddTask} className="add-task-button">
                  <FaPlus />
                  <span>Tilføj opgave</span>
                </button>
              </td>
            </tr>

            {/* Løste opgaver header */}
            {completedTasks.length > 0 && (
              <tr className="completed-tasks-header">
                <td colSpan="4" onClick={() => setShowCompleted(!showCompleted)}>
                  <div className="completed-tasks-toggle">
                    {showCompleted ? <FaChevronDown /> : <FaChevronRight />}
                    <span>Løste opgaver ({completedTasks.length})</span>
                  </div>
                </td>
              </tr>
            )}

            {/* Løste opgaver */}
            {showCompleted && completedTasks.map(task => (
              <tr key={task.id} className="completed">
                <td>
                  <button
                    className={`status-toggle ${task.completed ? 'completed' : ''}`}
                    onClick={() => toggleTaskStatus(task.id, task.completed)}
                  >
                    <FaCheck />
                  </button>
                </td>
                <td>
                  {editingTask === task.id ? (
                    <input
                      type="text"
                      className="task-name-input"
                      defaultValue={task.name}
                      autoFocus
                      onBlur={(e) => handleTaskNameChange(task.id, e.target.value)}
                      onKeyDown={(e) => handleKeyPress(e, task.id, e.target.value)}
                    />
                  ) : (
                    <span 
                      className="task-name"
                      onClick={() => setEditingTask(task.id)}
                    >
                      {task.name || 'Klik for at tilføje navn'}
                    </span>
                  )}
                </td>
                <td>
                  <span className={`status-badge ${task.completed ? 'completed' : 'pending'}`}>
                    {task.completed ? 'Udført' : 'Åben'}
                  </span>
                </td>
                <td>
                  <button
                    className="delete-task"
                    onClick={() => deleteTask(task.id)}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}

            {/* Tom state - vis kun hvis der slet ingen opgaver er */}
            {tasks.length === 0 && (
              <tr className="empty-state">
                <td colSpan="4">
                  Ingen opgaver tilføjet endnu
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ProjectTaskTable;
